import {
  Heading2, Heading3, List, ListOrdered, Quote, Code, Minus,
  Table as TableIcon, ListCollapse, ListTree, Image as ImageIcon, Video, Clapperboard, Link as LinkIcon, BookOpen,
  type LucideIcon,
} from 'lucide-react'
import { type Editor, type Range } from '@tiptap/react'
import type { DialogKind } from './dialogKinds'

export interface SlashItem {
  title: string
  subtitle: string
  icon: LucideIcon
  keywords: string[]
  run: (editor: Editor, range: Range) => void
}

// The slash-menu command set. Pure-editor actions run inline; the four that
// need user input delete the "/query" then open the matching dialog.
export function buildSlashItems(onOpenDialog: (kind: DialogKind) => void): SlashItem[] {
  const at = (editor: Editor, range: Range) => editor.chain().focus().deleteRange(range)
  const openAfter = (editor: Editor, range: Range, kind: DialogKind) => {
    at(editor, range).run()
    onOpenDialog(kind)
  }

  return [
    { title: 'Heading 2', subtitle: 'Section heading', icon: Heading2, keywords: [ 'h2', 'heading', 'title' ],
      run: (e, r) => at(e, r).setNode('heading', { level: 2 }).run() },
    { title: 'Heading 3', subtitle: 'Sub-section heading', icon: Heading3, keywords: [ 'h3', 'heading', 'subtitle' ],
      run: (e, r) => at(e, r).setNode('heading', { level: 3 }).run() },
    { title: 'Bullet list', subtitle: 'Unordered list', icon: List, keywords: [ 'bullet', 'list', 'ul' ],
      run: (e, r) => at(e, r).toggleBulletList().run() },
    { title: 'Numbered list', subtitle: 'Ordered list', icon: ListOrdered, keywords: [ 'numbered', 'ordered', 'list', 'ol' ],
      run: (e, r) => at(e, r).toggleOrderedList().run() },
    { title: 'Quote', subtitle: 'Block quote', icon: Quote, keywords: [ 'quote', 'blockquote', 'citation' ],
      run: (e, r) => at(e, r).toggleBlockquote().run() },
    { title: 'Code block', subtitle: 'Fenced code', icon: Code, keywords: [ 'code', 'pre', 'snippet' ],
      run: (e, r) => at(e, r).toggleCodeBlock().run() },
    { title: 'Divider', subtitle: 'Horizontal rule', icon: Minus, keywords: [ 'divider', 'hr', 'rule', 'separator' ],
      run: (e, r) => at(e, r).setHorizontalRule().run() },
    { title: 'Table', subtitle: '3×3 with header row', icon: TableIcon, keywords: [ 'table', 'grid' ],
      run: (e, r) => at(e, r).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
    { title: 'Collapsible section', subtitle: 'Expandable details', icon: ListCollapse, keywords: [ 'details', 'collapse', 'accordion', 'faq', 'toggle' ],
      run: (e, r) => at(e, r).setDetails().run() },
    { title: 'Table of contents', subtitle: 'Auto outline of headings', icon: ListTree, keywords: [ 'toc', 'contents', 'outline' ],
      run: (e, r) => at(e, r).insertTableOfContents().run() },
    { title: 'Image', subtitle: 'Upload or embed', icon: ImageIcon, keywords: [ 'image', 'img', 'photo', 'picture', 'upload' ],
      run: (e, r) => openAfter(e, r, 'image') },
    { title: 'Video', subtitle: 'Embed a YouTube video', icon: Video, keywords: [ 'video', 'youtube', 'embed', 'media' ],
      run: (e, r) => openAfter(e, r, 'video') },
    { title: 'Loom video', subtitle: 'Embed a Loom recording', icon: Clapperboard, keywords: [ 'loom', 'video', 'embed', 'recording', 'screen' ],
      run: (e, r) => openAfter(e, r, 'loom') },
    { title: 'Link', subtitle: 'Insert a hyperlink', icon: LinkIcon, keywords: [ 'link', 'url', 'href', 'anchor' ],
      run: (e, r) => openAfter(e, r, 'link') },
    { title: 'Link to article', subtitle: 'Link another help article', icon: BookOpen, keywords: [ 'article', 'internal', 'link', 'help' ],
      run: (e, r) => openAfter(e, r, 'article') },
  ]
}

export function filterSlashItems(items: SlashItem[], query: string): SlashItem[] {
  const q = query.trim().toLowerCase()
  if (!q) return items
  return items.filter(item =>
    item.title.toLowerCase().includes(q) || item.keywords.some(k => k.includes(q)),
  )
}
